import {
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { createErrorCopyAction } from "./ui/Toaster";
import { useTradeOperation } from "../hooks";
import { useMarkets } from "../hooks/useMarkets";
import { useTradePrice } from "../hooks/useTradePrice";
import { useBucketCollections } from "../hooks/useBucketCollections";
import { fromSatoshi } from "../sdk";
import { shortenId } from "../sdk/utils/display";
import { formatSizeInputValue } from "../sdk/utils/math";
import { getDisplayMarginType } from "../sdk/types/data";
import {
  DEFAULT_BASE_URL,
  DEFAULT_MNEMONIC,
  LEVERAGE_MIN,
  LEVERAGE_MAX,
  SIZE_PERCENT_MIN,
  SIZE_PERCENT_MAX,
  SIZE_PERCENT_STEP,
  PRICE_STEP,
  PRICE_MIN,
  CURRENCY_MIN_FRACTION_DIGITS,
  CURRENCY_MAX_FRACTION_DIGITS,
  SIZE_DISPLAY_DECIMALS,
  ORDER_TYPE_MARKET,
  ORDER_TYPE_LIMIT,
  MARGIN_MODE_CROSS,
  MARGIN_MODE_ISOLATED,
} from "./smartlayout/constants";
import { useSubscription } from "../context/SubscriptionContext";
import "./smartlayout/layout.css";

export interface TradeOperationRef {
  leverage: number;
  setLeverage: (leverage: number) => void;
  calculateMaxRatio: (tempLeverage: number) => string;
  changeLeverage: (
    newLeverage: number,
    options?: {
      bucketId?: string;
      leverageType?: string;
      reason?: string;
    },
  ) => Promise<void>;
  setPrice: (price: number | string) => void;
  setSizeInput: (size: number) => void;
  getSizeInput: () => number;
  getAvailableTradeAmount: () => number;
}

interface TradeOperationProps {
  baseURL?: string;
  mnemonic?: string;
  marketId?: string;
  marketSymbol?: string; // Optional - will be auto-derived from selected market if not provided
  selectedBucketBalance?: number; // Balance from selected bucket (nominalValue)
  selectedBucketId?: string | null; // Bucket ID for display in label
  onLeverageClick?: () => void; // Callback when leverage badge is clicked
  onNumberCalculatorClick?: () => void; // Callback when number calculator icon is clicked
  authToken?: string; // Auth token for API calls
  walletAddress?: string; // Wallet address for fetching bucket data
}

const TradeOperation = forwardRef<TradeOperationRef, TradeOperationProps>(
  function TradeOperation(
    {
      baseURL = DEFAULT_BASE_URL,
      mnemonic = DEFAULT_MNEMONIC,
      marketId,
      marketSymbol,
      selectedBucketBalance,
      selectedBucketId,
      onLeverageClick,
      onNumberCalculatorClick,
      authToken,
      walletAddress,
    },
    ref,
  ): JSX.Element {
    const { client: subscriptionClient } = useSubscription();
    // Get selected market info from useMarkets hook
    const { selectedMarketId, selectedMarketSymbol } = useMarkets({
      baseURL,
      autoFetchMarkets: true,
    });

    // Determine the market ID to use (prop takes precedence, then selectedMarketId)
    const effectiveMarketId = marketId || selectedMarketId || "";

    // Derive market symbol: prop > selectedMarketSymbol > fallback
    const effectiveMarketSymbol = marketSymbol || selectedMarketSymbol || "";

    // Get trade price from useTradePrice hook for market orders
    const { lastPrice: tradePriceData } = useTradePrice({
      subscriptionClient,
      baseURL,
      marketId: effectiveMarketId,
    });

    // Extract trade price for market orders
    const tradePrice = tradePriceData?.price || 0;
    // Ref to store the loading toast ID so we can dismiss it later
    const loadingToastIdRef = useRef<number>(0);
    // Ref to store the leverage change loading toast ID
    const leverageLoadingToastIdRef = useRef<number>(0);
    // Ref to track if we're currently changing leverage (to distinguish from order results)
    const isChangingLeverageRef = useRef<boolean>(false);

    // Get bucket collections data if walletAddress and authToken are provided
    const { collections, spotWalletBalance } = useBucketCollections({
      subscriptionClient,
      baseURL,
      walletAddress: walletAddress || "",
      authToken: authToken || "",
      useWebSocket: !!walletAddress && !!authToken, // Only use WebSocket if we have both
    });

    // Find the selected bucket from collections
    const selectedBucket = useMemo(() => {
      if (!selectedBucketId || !collections.length) return null;
      return (
        collections.find(
          (collection) => collection.bucketId === selectedBucketId,
        ) || null
      );
    }, [collections, selectedBucketId]);

    const {
      orderType,
      setOrderType,
      fundPercent,
      sizeInput,
      price,
      leverage,
      marginMode,
      availableBalance,
      currentPrice,
      isReady,
      loading,
      result,
      setFundPercent,
      setSizeInput,
      setPrice,
      setMarginMode,
      setLeverage,
      placeBuyOrder,
      placeSellOrder,
      changeLeverage: changeLeverageAPI,
      calculateValue,
      calculateMargin,
      calculateSizeUSD,
      getOrderPrice,
      getAvailableTradeAmount,
    } = useTradeOperation({
      baseURL,
      mnemonic,
      marketId: effectiveMarketId,
      marketSymbol: effectiveMarketSymbol,
      selectedBucketBalance,
      selectedBucketId: selectedBucketId || undefined, // Pass bucket ID for order placement
      tradePrice, // Pass trade price for market orders
    });

    // Memoize calculations from hook - depend on actual state values for proper updates
    const orderPrice = useMemo(
      () => getOrderPrice(),
      [getOrderPrice, orderType, price, currentPrice],
    );
    const availableTradeAmount = useMemo(
      () => getAvailableTradeAmount(),
      [getAvailableTradeAmount, availableBalance, leverage],
    );
    const value = useMemo(
      () => calculateValue(),
      [
        calculateValue,
        sizeInput,
        fundPercent,
        availableBalance,
        orderPrice,
        leverage,
      ],
    );
    const margin = useMemo(
      () => calculateMargin(),
      [calculateMargin, leverage, value],
    );

    // Convert value and margin from satoshi to decimal for display
    const valueDecimal = useMemo(() => {
      return fromSatoshi(String(value));
    }, [value]);

    const marginDecimal = useMemo(() => {
      return fromSatoshi(String(margin));
    }, [margin]);

    // Memoize currency formatting options
    const currencyFormatOptions = useMemo(
      () => ({
        minimumFractionDigits: CURRENCY_MIN_FRACTION_DIGITS,
        maximumFractionDigits: CURRENCY_MAX_FRACTION_DIGITS,
      }),
      [],
    );

    // Memoize current price display
    const currentPriceDisplay = useMemo(
      () => currentPrice.toFixed(CURRENCY_MIN_FRACTION_DIGITS),
      [currentPrice],
    );

    // Calculate total account equity (spot + all buckets)
    const accountEquity = useMemo(() => {
      const totalBucketsEquity = collections.reduce(
        (sum, collection) => sum + collection.totalEquity,
        0,
      );
      return spotWalletBalance + totalBucketsEquity;
    }, [collections, spotWalletBalance]);

    // Memoize formatted values for bucket info display
    const accountEquityDisplay = useMemo(
      () => accountEquity.toLocaleString("en-US", currencyFormatOptions),
      [accountEquity, currencyFormatOptions],
    );

    const spotBalanceDisplay = useMemo(
      () => spotWalletBalance.toLocaleString("en-US", currencyFormatOptions),
      [spotWalletBalance, currencyFormatOptions],
    );

    const selectedBucketBalanceDisplay = useMemo(
      () =>
        selectedBucket
          ? selectedBucket.totalEquity.toLocaleString(
              "en-US",
              currencyFormatOptions,
            )
          : "--",
      [selectedBucket, currencyFormatOptions],
    );

    const bucketBalanceDisplay = useMemo(
      () =>
        selectedBucket
          ? selectedBucket.totalEquity.toLocaleString(
              "en-US",
              currencyFormatOptions,
            )
          : "--",
      [selectedBucket, currencyFormatOptions],
    );

    const unrealizedPnLDisplay = useMemo(
      () =>
        selectedBucket
          ? selectedBucket.unrealizedPnL.toLocaleString(
              "en-US",
              currencyFormatOptions,
            )
          : "--",
      [selectedBucket, currencyFormatOptions],
    );

    const crossMarginRatioDisplay = useMemo(
      () =>
        selectedBucket
          ? (selectedBucket.marginRatio * 100).toFixed(
              CURRENCY_MIN_FRACTION_DIGITS,
            ) + "%"
          : "--",
      [selectedBucket],
    );

    const maintenanceMarginDisplay = useMemo(
      () =>
        selectedBucket
          ? selectedBucket.usedMargin.toLocaleString(
              "en-US",
              currencyFormatOptions,
            )
          : "--",
      [selectedBucket, currencyFormatOptions],
    );

    const crossAccountLeverageDisplay = useMemo(
      () =>
        selectedBucket
          ? `${selectedBucket.leverage.toFixed(CURRENCY_MIN_FRACTION_DIGITS)}x`
          : "--",
      [selectedBucket],
    );

    const shortenedBucketId = useMemo(
      () => shortenId(selectedBucketId || null),
      [selectedBucketId],
    );

    // Automatically switch margin mode based on selected bucket
    useEffect(() => {
      if (selectedBucket) {
        const bucketMarginType = getDisplayMarginType(selectedBucket);
        if (
          bucketMarginType === MARGIN_MODE_CROSS ||
          bucketMarginType === MARGIN_MODE_ISOLATED
        ) {
          setMarginMode(bucketMarginType);
        }
      }
    }, [selectedBucket, setMarginMode]);



    const handleOrderTypeMarket = useCallback(() => {
      setOrderType(ORDER_TYPE_MARKET);
    }, [setOrderType]);

    const handleOrderTypeLimit = useCallback(() => {
      setOrderType(ORDER_TYPE_LIMIT);
    }, [setOrderType]);

    const handleFundPercentIncrease = useCallback(() => {
      setFundPercent(
        Math.min(SIZE_PERCENT_MAX, fundPercent + SIZE_PERCENT_STEP),
      );
    }, [fundPercent, setFundPercent]);

    const handleFundPercentDecrease = useCallback(() => {
      setFundPercent(
        Math.max(SIZE_PERCENT_MIN, fundPercent - SIZE_PERCENT_STEP),
      );
    }, [fundPercent, setFundPercent]);

    // Use the hook's handleFundPercentChange directly - it already updates sizeInput
    const handleFundPercentChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const percent = parseInt(e.target.value);
        setFundPercent(percent); // This will update sizeInput via the hook
      },
      [setFundPercent],
    );

    const handlePriceChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrice(parseFloat(e.target.value));
      },
      [setPrice],
    );

    // Track if size input is focused and the value while user is typing
    const [isSizeInputFocused, setIsSizeInputFocused] = useState(false);
    const [sizeInputDisplayValue, setSizeInputDisplayValue] =
      useState<string>("");

    const handleSizeInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Store what user is typing (allows partial input like "1." or "1.12")
        setSizeInputDisplayValue(inputValue);
        // Update the actual sizeInput value (for calculations)
        const val = parseFloat(inputValue);
        const roundedVal = isNaN(val) ? 0 : Math.round(val * 1000000) / 1000000;
        setSizeInput(roundedVal); // This will update fundPercent via the hook
      },
      [setSizeInput],
    );

    const handleSizeInputFocus = useCallback(() => {
      setIsSizeInputFocused(true);
      // When focusing, initialize with current formatted value
      setSizeInputDisplayValue(formatSizeInputValue(sizeInput));
    }, [sizeInput]);

    const handleSizeInputBlur = useCallback(() => {
      setIsSizeInputFocused(false);
      // When blurring, parse and format the final value
      const val = parseFloat(sizeInputDisplayValue);
      //const roundedVal = isNaN(val) ? 0 : Math.round(val * 1000000) / 1000000;
      setSizeInput(val);
      // Clear display value so it will use formatted sizeInput when not focused
      setSizeInputDisplayValue("");
    }, [sizeInputDisplayValue, setSizeInput]);

    // Handle buy order with immediate toast notification
    const handleBuyOrder = useCallback(() => {
      const toastId = toast.loading("Submit transaction...", {
        description: "Placing buy order",
      });
      loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;
      placeBuyOrder();
    }, [placeBuyOrder]);

    // Handle sell order with immediate toast notification
    const handleSellOrder = useCallback(() => {
      const toastId = toast.loading("Submit transaction...", {
        description: "Placing sell order",
      });
      loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;
      placeSellOrder();
    }, [placeSellOrder]);

    // Handle leverage change with API call
    const handleChangeLeverage = useCallback(
      async (
        newLeverage: number,
        options?: {
          bucketId?: string;
          leverageType?: string;
          reason?: string;
        },
      ) => {
        // Set flag to indicate we're changing leverage
        isChangingLeverageRef.current = true;

        // Show loading toast
        const toastId = toast.loading("Changing leverage...", {
          description: `Updating leverage to ${newLeverage}x`,
        });
        leverageLoadingToastIdRef.current =
          typeof toastId === "number" ? toastId : 0;

        try {
          // Determine leverage type from margin mode if not provided
          const leverageType =
            options?.leverageType ||
            (marginMode === MARGIN_MODE_CROSS ? "cross" : "isolated");

          // Use selectedBucketId if bucketId is not provided
          const bucketId = options?.bucketId || selectedBucketId || "";

          await changeLeverageAPI(newLeverage, {
            ...options,
            bucketId,
            leverageType,
          });

          // The result will be handled by the useEffect below
        } catch (error) {
          // Reset flag on error
          isChangingLeverageRef.current = false;

          // Dismiss loading toast
          if (leverageLoadingToastIdRef.current !== 0) {
            toast.dismiss(leverageLoadingToastIdRef.current);
            leverageLoadingToastIdRef.current = 0;
          }

          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";
          toast.error("Leverage Change Failed", {
            description: errorMessage,
            action: createErrorCopyAction(errorMessage),
          });
        }
      },
      [changeLeverageAPI, marginMode, selectedBucketId],
    );

    // Expose leverage and setLeverage via ref
    useImperativeHandle(
      ref,
      () => ({
        leverage,
        setLeverage,
        calculateMaxRatio: (tempLeverage: number) => {
          const sizeUSD = calculateSizeUSD();
          const orderValueAtLeverage = sizeUSD;
          return availableBalance > 0 && orderValueAtLeverage > 0
            ? (orderValueAtLeverage / availableBalance).toFixed(
                CURRENCY_MIN_FRACTION_DIGITS,
              )
            : tempLeverage.toFixed(CURRENCY_MIN_FRACTION_DIGITS);
        },
        changeLeverage: handleChangeLeverage,
        setPrice: (price: number | string) => {
          const numPrice =
            typeof price === "number" ? price : parseFloat(price);
          if (!isNaN(numPrice)) {
            setPrice(numPrice);
          }
        },
        setSizeInput: (size: number) => {
          setSizeInput(size);
        },
        getSizeInput: () => sizeInput,
        getAvailableTradeAmount: () => availableTradeAmount,
      }),
      [
        leverage,
        setLeverage,
        calculateSizeUSD,
        getOrderPrice,
        availableBalance,
        handleChangeLeverage,
        setPrice,
        setSizeInput,
        sizeInput,
        availableTradeAmount,
      ],
    );

    // Show toast notifications when result changes (handles both orders and leverage changes)
    useEffect(() => {
      if (!result) return;

      // Check if this is a leverage change result
      if (isChangingLeverageRef.current) {
        // Reset flag
        isChangingLeverageRef.current = false;

        // Dismiss the leverage loading toast if it exists
        if (leverageLoadingToastIdRef.current !== 0) {
          toast.dismiss(leverageLoadingToastIdRef.current);
          leverageLoadingToastIdRef.current = 0;
        }

        if (result.success) {
          const description = result.orderId
            ? `Transaction: ${result.orderId}`
            : result.message || "Leverage changed successfully";
          toast.success("Leverage Changed Successfully", {
            description,
          });
        } else if (result.error) {
          toast.error("Leverage Change Failed", {
            description: result.error,
            action: createErrorCopyAction(result.error || ""),
          });
        }
      } else {
        // This is an order result
        // Dismiss the order loading toast if it exists
        if (loadingToastIdRef.current !== 0) {
          toast.dismiss(loadingToastIdRef.current);
          loadingToastIdRef.current = 0;
        }

        if (result.success) {
          const description = result.orderId
            ? `Order ID: ${result.orderId}`
            : result.message || "Order placed successfully";
          toast.success("Order Placed Successfully", {
            description,
          });
        } else if (result.error) {
          toast.error("Order Failed", {
            description: result.error,
            action: createErrorCopyAction(result.error || ""),
          });
        }
      }
    }, [result]);

    return (
      <div className="component">
        <h2>{effectiveMarketSymbol}</h2>
        {/* Trade Configuration Panel */}
        <div className="trade-op-config">
          <div className="trade-op-badge">
            <span className="trade-op-badge-label">{marginMode}</span>
            <span className="trade-op-info-icon">i</span>
          </div>
          <div
            className="trade-op-badge"
            onClick={onLeverageClick}
            style={{ cursor: "pointer" }}
          >
            <span className="trade-op-badge-label">{leverage}x</span>
            <span className="trade-op-info-icon">i</span>
          </div>
        </div>
        {/* Order Type Selection */}
        <div className="trade-op-order-type-selector">
          <button
            onClick={handleOrderTypeMarket}
            className={`btn-toggle ${orderType === ORDER_TYPE_MARKET ? "active" : ""}`}
          >
            MARKET
          </button>
          <button
            onClick={handleOrderTypeLimit}
            className={`btn-toggle ${orderType === ORDER_TYPE_LIMIT ? "active" : ""}`}
          >
            LIMIT
          </button>
        </div>
        {/* Limit Price Input (only for limit orders) */}
        {orderType === ORDER_TYPE_LIMIT && (
          <div className="trade-op-form-group">
            <div className="trade-op-form-row">
              <label htmlFor="price" className="trade-op-label">
                Price:
              </label>
              <input
                id="price"
                type="number"
                value={price}
                onChange={handlePriceChange}
                placeholder={currentPriceDisplay}
                step={PRICE_STEP}
                min={PRICE_MIN}
              />
            </div>
          </div>
        )}

        {/* Order Size Input and Slider */}
        <div className="trade-op-form-group">
          {/* Size controls - on a new row */}
          <div className="trade-op-form-row" style={{ marginBottom: "0.5rem" }}>
            <label htmlFor="size" className="trade-op-label">
              Size:
            </label>
            <span className="trade-op-icon" onClick={onNumberCalculatorClick}>
              <img src="cal.svg" />
            </span>
            <div className="trade-op-size-controls">
              <input
                id="size"
                type="number"
                step="0.000001"
                value={isSizeInputFocused ? sizeInputDisplayValue : sizeInput}
                onChange={handleSizeInputChange}
                onFocus={handleSizeInputFocus}
                onBlur={handleSizeInputBlur}
                placeholder="0"
                min={PRICE_MIN}
                max={availableTradeAmount}
                className="trade-op-input"
              />
              <div className="trade-op-size-buttons">
                <button
                  onClick={handleFundPercentIncrease}
                  className="btn-small"
                >
                  ▲
                </button>
                <button
                  onClick={handleFundPercentDecrease}
                  className="btn-small"
                >
                  ▼
                </button>
              </div>
            </div>
          </div>
          <input
            type="range"
            min={SIZE_PERCENT_MIN}
            max={SIZE_PERCENT_MAX}
            value={fundPercent}
            onChange={handleFundPercentChange}
            step={SIZE_PERCENT_STEP}
            style={
              {
                width: "100%",
                "--slider-progress": `${((fundPercent - SIZE_PERCENT_MIN) / (SIZE_PERCENT_MAX - SIZE_PERCENT_MIN)) * 100}%`,
              } as React.CSSProperties
            }
            className="filled-slider"
          />
          <div className="trade-op-slider-labels">
            <span>0</span>
            <span>20</span>
            <span>40</span>
            <span>60</span>
            <span>80</span>
            <span>100</span>
          </div>
        </div>

        {/* Buy/Sell Action Panels */}
        <div className="trade-op-action-panel">
          {/* Buy Panel */}
          <div>
            <div className="trade-op-panel-title buy">LONG</div>
            <div className="trade-op-panel-size">
              {sizeInput.toFixed(3)}/100.000
            </div>
            <button
              onClick={handleBuyOrder}
              disabled={loading || !isReady || value <= 0}
              className="btn-buy"
            >
              {loading ? "PLACING..." : "BUY LONG"}
            </button>
            <div className="trade-op-panel-details">
              <div>Value {valueDecimal.toFixed(2)}</div>
              <div>Margin {marginDecimal.toFixed(2)}</div>
            </div>
          </div>

          {/* Sell Panel */}
          <div>
            <div className="trade-op-panel-title sell">SHORT</div>
            <div className="trade-op-panel-size">
              {sizeInput.toFixed(3)}/100.000
            </div>
            <button
              onClick={handleSellOrder}
              disabled={loading || !isReady || value <= 0}
              className="btn-sell"
            >
              {loading ? "PLACING..." : "SELL SHORT"}
            </button>
            <div className="trade-op-panel-details">
              <div>
                Value {valueDecimal.toFixed(CURRENCY_MIN_FRACTION_DIGITS)}
              </div>
              <div>
                Margin {marginDecimal.toFixed(CURRENCY_MIN_FRACTION_DIGITS)}
              </div>
            </div>
          </div>
        </div>
        {/* Available Funds - on its own row */}
        <div className="trade-op-info-panel">
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Account Equity:</span>
            <span className="trade-op-info-value">
              {accountEquityDisplay} USDC
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Spot:</span>
            <span className="trade-op-info-value">
              {spotBalanceDisplay} USDC
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">
              {selectedBucketId || "Bucket"}:
            </span>
            <span className="trade-op-info-value">
              {selectedBucketBalanceDisplay} USDC
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">
              Bucket Overview: {shortenedBucketId}
            </span>
          </div>

          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Balance:</span>
            <span className="trade-op-info-value">
              {bucketBalanceDisplay} USDC
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Unrealized PNL:</span>
            <span className="trade-op-info-value">
              {unrealizedPnLDisplay} USDC
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Cross Margin Ratio:</span>
            <span className="trade-op-info-value">
              {crossMarginRatioDisplay}
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Maintenance Margin:</span>
            <span className="trade-op-info-value">
              {maintenanceMarginDisplay} USDC
            </span>
          </div>
          <div className="trade-op-info-row">
            <span className="trade-op-info-label">Cross Account Leverage:</span>
            <span className="trade-op-info-value">
              {crossAccountLeverageDisplay}
            </span>
          </div>
        </div>
      </div>
    );
  },
);

export type TradeOperationComponent = typeof TradeOperation;

export default TradeOperation;
